import React, { useState } from 'react';
import { Button, Form, Modal, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { fetchEmissionDataUploads } from './uploadDataSlice';
import UploadDataConfirmForm from './UploadDataConfirmForm';
import * as yup from 'yup';
import { createEmissionDataApi, fetchEmissionDataUploadsApi, uploadEmissionDataApi } from './uploadDataApi';
import { toast } from 'react-toastify';
import LoadingButton from '../../../components/LoadingButton';
import { FiInfo, FiClipboard } from 'react-icons/fi';

const TEMPLATE_TYPE = {
  STANDARD: 'STANDARD',
  CUSTOM: 'CUSTOM',
  BID: 'BID'
};
const SUPPORTED_FORMATS = ['text/csv', 'application/vnd.ms-excel', 'text/x-csv', 'text/plain', '.csv', 'csv'];

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: '#ffc107',
  color: '#fff',
  fontSize: '12px',
  fontWeight: 'bold'
};

const UploadDataForm = ({ showModal, closeModal, onUploadHandler }) => {
  const { t } = useTranslation();
  const schema = yup.object().shape({
    selectedFile: yup
      .mixed()
      .required()
      .required(t('File is required'))
      .test('fileFormat', t('Unsupported File Format!'), (value) => {
        const type = !!value ? (!!value.type ? value.type : value.name.split('.').slice(-1).pop()) : null;
        return !!value && SUPPORTED_FORMATS.includes(type);
      })
  });
  const {
    control,
    clearErrors,
    setValue,
    getValues,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange'
  });
  const dispatch = useDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const orgId = useSelector((state) => state.users.user.usrOrgId);
  // const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileDescription, setSelectedFileDescription] = useState();
  const [templateType, setTemplateType] = useState(TEMPLATE_TYPE.STANDARD);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  useWatch({ name: 'selectedFile', control });
  const canUpload = !!getValues('selectedFile') && selectedFileDescription && !isUploading && isValid;

  const resetForm = () => {
    setSelectedFileDescription(null);
    setValue('selectedFile', null);
    setTemplateType(TEMPLATE_TYPE.STANDARD);
    setIsUploading(false);
    clearErrors(['selectedFile']);
  };

  const closeModalHandler = () => {
    resetForm();
    setShowConfirmationModal(false);
    closeModal();
  };

  const handleTemplateChange = (event) => setTemplateType(event.target.value);

  const handleUploadData = async () => {
    setIsUploading(true);
    try {
      const newFile = getValues('selectedFile');
      const listUploadedFiles = await fetchEmissionDataUploadsApi(orgId);
      listUploadedFiles.sort((a, b) => a.upldflId - b.upldflId);
      const duplicateFiles = listUploadedFiles.find(file => newFile.name === file.upldflName);
      if (duplicateFiles) {
        setUploadedFile(newFile);
        closeModal();
        setShowConfirmationModal(true);
      } else {
        await processUpload();
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadDataHandler = async () => {
    setIsUploading(true);
    try {
      await processUpload();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const processUpload = async () => {
    const newFile = getValues('selectedFile') || uploadedFile;
    const { key, name } = await uploadEmissionDataApi(orgId, newFile);
    const data = ({
      key,
      name,
      desc: selectedFileDescription,
      templateType
    });
    await createEmissionDataApi(orgId, data);
    dispatch(fetchEmissionDataUploads());
    setShowConfirmationModal(false);
    closeModalHandler();
    if (onUploadHandler) {
      await onUploadHandler();
    }
  };

  return (
    <Container>
      <Modal show={showModal} onHide={closeModalHandler} className="sisde-modal emission-upload-modal">
        <Modal.Header>
          <Modal.Title>{t('Upload Emission Data')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className='alert alert-warning' style={{backgroundColor: 'rgb(255, 243, 205, 0.3)', borderColor: '#ffeeba'}}>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <span style={badgeStyle}><FiInfo size={12} /></span>
                <strong>{t('Important Information')}</strong>
              </div>
              <div>
                <strong>{t('Supported date formats:')}</strong>{' '}
                {t('dd/MM/yyyy, yyyy/MM/dd, dd/MMM/yyyy (e.g. 30/05/2025). Separators: /, -, ., \\, or space.')}
              </div>
              <hr />
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <FiClipboard size={16} />
                <strong>{t('Upload Guidelines')}</strong>
              </div>
              <ol className="unstyled-numbered-list" style={{ listStyle: 'none', padding: 0, marginBottom: 0 }}>
                <li className="d-flex" style={{ gap: '8px', marginTop: '8px' }}>
                  <span style={badgeStyle}>1</span>
                  <span>{t('Upload your CSV file to import emission data.')}</span>
                </li>
                <li className="d-flex" style={{ gap: '8px', marginTop: '8px' }}>
                  <span style={badgeStyle}>2</span>
                  <span>{t('If validation fails, the system will generate an Error File containing only the invalid records. Correct the errors and upload the file again.')}</span>
                </li>
                <li className="d-flex" style={{ gap: '8px', marginTop: '8px' }}>
                  <span style={badgeStyle}>3</span>
                  <span>{t('You can upload the downloaded Error File directly. The Error column will be removed automatically before validation, so no manual changes to the file structure are required.')}</span>
                </li>
                <li className="d-flex" style={{ gap: '8px', marginTop: '8px' }}>
                  <span style={badgeStyle}>4</span>
                  <span>{t('If you upload a file with the same file name, the system will replace all data imported from the previous upload with the data from the latest upload.')}</span>
                </li>
              </ol>
            </div>
            <Form.Group style={{ marginBottom: '10px' }}>
              <Controller
                name="selectedFile"
                control={control}
                render={({ field }) => {
                  const handleSelectFile = (e) => field.onChange(e.target.files[0]);
                  return (
                    <Form.File
                      id="emissionFileUploadControl"
                      label={t("Select File")}
                      accept=".csv"
                      className="form-content"
                      onChange={handleSelectFile}
                      isInvalid={errors.selectedFile}
                    />
                  );
                }}
              />
              {errors.selectedFile && <span className="required-span">{errors.selectedFile.message}</span>}
            </Form.Group>
            <Form.Group className="custom-form-control d-flex flex-wrap" style={{ gap: '24px' }}>
              <Form.Check
                type="radio"
                id={TEMPLATE_TYPE.STANDARD}
                value={TEMPLATE_TYPE.STANDARD}
                checked={templateType === TEMPLATE_TYPE.STANDARD}
                label={t('Standard Template')}
                onChange={handleTemplateChange}
              />

              <Form.Check
                type="radio"
                id={TEMPLATE_TYPE.CUSTOM}
                value={TEMPLATE_TYPE.CUSTOM}
                checked={templateType === TEMPLATE_TYPE.CUSTOM}
                label={t('Custom Template')}
                onChange={handleTemplateChange}
              />

              <Form.Check
                type="radio"
                id={TEMPLATE_TYPE.BID}
                value={TEMPLATE_TYPE.BID}
                checked={templateType === TEMPLATE_TYPE.BID}
                label={t('BID energy Template')}
                onChange={handleTemplateChange}
              />
            </Form.Group>
            <Form.Group className="custom-form-control">
              <Form.Label> {t('Description')} *</Form.Label>
              <Form.Control
                value={selectedFileDescription}
                onChange={(e) => setSelectedFileDescription(e.target.value)}
                type="text"
                as="textarea"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <LoadingButton isLoading={isUploading} />
          <Button variant="secondary" onClick={closeModalHandler}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" onClick={handleUploadData} disabled={!canUpload}>
            {t('Upload File')}
          </Button>
        </Modal.Footer>
      </Modal>
      <UploadDataConfirmForm
        showConfirmationModal={showConfirmationModal}
        closeConfirmationModal={closeModalHandler}
        uploadDataHandler={uploadDataHandler}
        isUploading={isUploading}
      />
    </Container>
  );
};

export default UploadDataForm;
